import React from 'react';
import { Menu, Home, Info, ReceiptText, Images, FileSearch } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@photo-booth/ui/sheet';
import { Button } from '@photo-booth/ui/button';
import { Separator } from '@photo-booth/ui/separator';

interface HamburgerMenuProps {
  onNavigate?: (path: string) => void;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ onNavigate }) => {
  const menuItems = [
    {
      icon: Home,
      label: 'Home',
      path: '/',
      description: 'Go to home page'
    },
    {
      icon: Info,
      label: 'About',
      path: '/about-us',
      description: 'Learn more about us'
    },
    {
      icon: ReceiptText,
      label: 'Terms & Privacy Policy',
      path: '/terms-and-privacy-policy',
      description: 'Read our terms and privacy policy'
    },
    {
      icon: Images,
      label: 'My photos',
      path: '/history',
      description: 'Your recent photo sessions'
    },
    // Beam API access is still pending — uncomment (and import FileSearch
    // from lucide-react) once slip verification is available.
    {
      icon: FileSearch,
      label: 'Find my photo',
      path: '/recover',
      description: 'Recover a photo session from a payment slip'
    }
  ];

  const handleItemClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-14 w-14 rounded-md focus:outline-none"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 sm:w-96">
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl font-semibold">Menu</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-1">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div key={item.path}>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-auto p-4 hover:bg-gray-50 focus:bg-gray-50"
                  onClick={() => handleItemClick(item.path)}
                >
                  <div className="flex items-center space-x-3">
                    <IconComponent className="h-5 w-5 text-gray-600" />
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{item.label}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </div>
                  </div>
                </Button>
                {index < menuItems.length - 1 && <Separator className="my-1" />}
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};
